import { FormControlLabel, Grid, Radio, RadioGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import NumberField from "components/number-field";
import React, { FC } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { AccountingType } from "v2-types/order";
import { ChargeFormEntry } from "views/orders/charges/components/GeneralChargesTable/types";
import { chargesPayeePayerForm } from "../services";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme: any) => ({
  chargesContainer: {
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    [theme.breakpoints.up('md')]: {
      height: '60px',
    },
    [theme.breakpoints.down('md')]: {
      paddingBottom: theme.spacing(1),
    },
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },

      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        padding: 8,
      },
    },
  },
  headerRowResponsive: {
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
    gap: theme.spacing(1),
  },
  descriptionRowMobile: {
    [theme.breakpoints.up('md')]: {
      flex: 1,
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    whiteSpace: 'normal',
  },
  amountRadioRow: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
}));

type Props = {
  methods: UseFormReturn<chargesPayeePayerForm>
  charge?: ChargeFormEntry;
  accountingEntry?: AccountingType;
  isMultiCharge?: boolean;
}

export const Header: FC<Props> = ({ methods, charge, accountingEntry, isMultiCharge }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!charge && !accountingEntry) return null;

  let description = charge?.description;
  let amountOverride = charge?.amount_override;

  if (!description && accountingEntry) description = accountingEntry.description;
  if (!amountOverride && accountingEntry) amountOverride = accountingEntry.amount_override;

  const numberFieldNode = (
    <NumberField
      color="secondary"
      className={classes.numberField}
      isAllowed={undefined}
      prefix="$"
      disabled
      thousandSeparator
      variant="outlined"
      value={amountOverride}
    />
  );

  const radioGroupNode = (
    <Controller
      control={methods.control}
      name="typeAmount"
      render={({ field }) => (
        <RadioGroup {...field} row>
          {!isMultiCharge && (
            <FormControlLabel
              value="Amount"
              control={<Radio />}
              label={<Typography variant="subtitle2">{t('orders:amountLabel')}</Typography>}
            />
          )}
          <FormControlLabel
            value="Percent"
            control={<Radio />}
            label={<Typography variant="subtitle2">{t('orders:percentLabel')}</Typography>}
          />
        </RadioGroup>
      )}
    />
  );

  return (
    <Grid className={classes.chargesContainer}>
      <Grid container direction="column">
        <Grid item xs={12}>
          {isMobile ? (
            <Grid container direction="column" className={classes.headerRowResponsive}>
              <Grid item className={classes.descriptionRowMobile}>
                <Typography variant="body2">{description}</Typography>
              </Grid>
              <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={classes.amountRadioRow}
              >
                <Grid item xs={4}>
                  {numberFieldNode}
                </Grid>
                <Grid item>
                  {radioGroupNode}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              direction="row"
              spacing={1}
              justifyContent="space-between"
              wrap="nowrap"
            >
              <Grid item style={{ alignContent: 'center' }}>
                <Typography variant="body2">{description}</Typography>
              </Grid>

              <Grid
                item
                container
                direction="row"
                xs={5}
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                wrap="nowrap"
              >
                <Grid item xs={3}>
                  {numberFieldNode}
                </Grid>
                <Grid item>
                  {radioGroupNode}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
